import { GetKybDataResponse } from "@services/models/Kyb/GetKybData";
import {
  GetInstitutionalKYBResponse,
  KybLinksResponse,
} from "@services/models/Kyb/GetKybLinks";
import {
  MapUserIdToKYBRequest,
  MapUserIdToKYBResponse,
} from "@services/models/Kyb/MapUserIdToKyb";
import { UnmapUserIdFromKYBResponse } from "@services/models/Kyb/UnmapUserIdToKyb";
import { createApiClient } from "../api/createApiClient";
import { AxiosInstance } from "axios";
import secureLocalStorage from "react-secure-storage";

class KybApiService {
  private api: AxiosInstance;
  constructor() {
    this.api = createApiClient();
  }
  // GET /listAllKYBRecords
  // PUT /mapUserIdToKYB      body: { kybId, userId }

  public getKybLinks = async (
    page: number,
    limit: number,
  ): Promise<KybLinksResponse> => {
    try {
      const response = await this.api.get<KybLinksResponse>(
        "/adminPanel/listAllKYBRecords",
        {
          params: {
            page,
            limit,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching KYB links:", error);
      throw error;
    }
  };

  public async getKybData(id: number | string): Promise<GetKybDataResponse> {
    try {
      const res = await this.api.get<GetKybDataResponse>(
        `/user/getInstitutionalKYB/${encodeURIComponent(String(id))}`,
      );
      return res.data;
    } catch (error) {
      // You can add centralized logging here if desired
      throw error;
    }
  }
  public async saveKybData<SaveKybDataResponse>(
    payload: Record<string, any>,
    isSubmitted: boolean,
    applicationid?: number | string,
  ): Promise<SaveKybDataResponse> {
    try {
      const jwt = secureLocalStorage.getItem("jwt");
      const response = await this.api.put<SaveKybDataResponse>(
        "/adminPanel/updateKYBRecord",
        { kyb_data: payload, is_submitted: isSubmitted, id: applicationid },
        {
          headers: {
            "Content-Type": "application/json",
            ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async mapUserIdToKYB(
    payload: MapUserIdToKYBRequest,
  ): Promise<MapUserIdToKYBResponse> {
    const kybId = String(payload.kybId).trim();
    const userId = String(payload.userId).trim();
    if (!kybId || !userId) {
      throw new Error("mapUserIdToKYB: 'kybId' and 'userId' are required");
    }

    try {
      const res = await this.api.put<MapUserIdToKYBResponse>(
        "adminPanel/mapUserIdToKYB",
        { kybId, userId },
        { headers: { "Content-Type": "application/json" } },
      );
      return res.data;
    } catch (error) {
      throw error;
    }
  }

  public async deleteKybRecord(kybId: number | string): Promise<any> {
    try {
      const res = await this.api.delete(`/adminPanel/deleteKYB`, {
        data: { kybId },
      });
      return res.data;
    } catch (error) {
      throw error;
    }
  }

  public async getInstitutionalKYB(
    id: number | string,
  ): Promise<GetInstitutionalKYBResponse> {
    try {
      const res = await this.api.get<GetInstitutionalKYBResponse>(
        `/user/getInstitutionalKYB/${encodeURIComponent(String(id))}`,
      );
      return res.data;
    } catch (error) {
      // You can add centralized logging here if desired
      throw error;
    }
  }
  public async unmapUserIdFromKYB(
    kybId: string,
  ): Promise<UnmapUserIdFromKYBResponse> {
    const res = await this.api.put<UnmapUserIdFromKYBResponse>(
      "/adminPanel/unmapUserIdFromKYB",
      { kybId },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return res.data;
  }
}

export const kybService = new KybApiService();
